// The creature hover card: one global node that shows a creature's rolls as
// pills, each with its exact numbers on hover. It opens after 300 ms over anything carrying data-open
// (a creature id), data-creature-id, data-listing-id (a market listing) or
// data-showcase (a creature someone showed in chat, from its snapshot) or
// data-snap (a snapshot registered with setSnapshot, such as a released catch),
// stays open while the pointer moves into it, and closes 150 ms after the
// pointer leaves both, or on Esc. Game updates re-render its content in place
// and never close it. On touch, a 400 ms long-press opens it; a tap still
// opens the creature.
import { SPECIES, linePos } from '../shared/species.ts';
import { GROUND, AREA } from '../shared/grounds.ts';
import { SHRINE_TIERS } from '../shared/shrines.ts';
import { statWeights } from '../shared/rating.ts';
import { creatureXpToNext } from '../shared/xp.ts';
import type { CreatureDTO, ListingDTO } from '../shared/protocol.ts';
import { S } from './state.ts';
import { boostMark } from './auracharm.ts';
import { provenanceLine } from './provenance.ts';
import { spriteUrl } from './assets.ts';
import { esc, full, elChip, cname, nameHtml, summary } from './fmt.ts';
import { patch } from './dom.ts';
import { rollPills } from './rollpills.ts';
import { refineOf } from '../shared/refine.ts';
import { trainOf } from '../shared/train.ts';
import { placeBeside } from './tip.ts';

const ANCHOR = '[data-open],[data-creature-id],[data-listing-id],[data-showcase],[data-snap]';
const OPEN_MS = 300, CLOSE_MS = 150, PRESS_MS = 400;
const SHORT = ['HP', 'Atk', 'Def', 'Mag', 'Res', 'Spd'];

let card: HTMLElement | null = null;
let anchor: HTMLElement | null = null;
/** What the card shows: a creature id, a market listing id, or a chat showcase id. */
let showing: { creature?: number; listing?: number; showcase?: number; snap?: string } | null = null;

/** Creatures that are no longer in the roster but still show a card (a catch released on the spot). */
const snapshots = new Map<string, CreatureDTO>();
/** Whose each snapshot is, when it is another trainer's: a click on it opens the read-only creature view (client/peek.ts). */
const snapOwners = new Map<string, string>();
export function setSnapshot(key: string, c: CreatureDTO, owner?: string) {
  if (owner) snapOwners.set(key, owner);
  if (snapshots.get(key) === c) return;
  if (snapshots.size > 500) { const k = snapshots.keys().next().value!; snapshots.delete(k); snapOwners.delete(k); }
  snapshots.set(key, c);
}
/** A registered snapshot and its owner, or null when it has none (an own catch released on the spot). */
export function ownedSnapshot(key: string): { c: CreatureDTO; owner: string } | null {
  const c = snapshots.get(key), owner = snapOwners.get(key);
  return c && owner ? { c, owner } : null;
}
let openTimer = 0, closeTimer = 0, pressTimer = 0;
let swallowClick = false;

function node() {
  if (!card) {
    card = document.createElement('div');
    card.id = 'hovercard'; card.className = 'hc'; card.hidden = true;
    card.setAttribute('role', 'tooltip');
    card.addEventListener('pointerenter', () => clearTimeout(closeTimer));
    card.addEventListener('pointerleave', (e) => { if (e.pointerType !== 'touch') scheduleClose(); });
    document.body.appendChild(card);
  }
  return card;
}

function target(el: HTMLElement) {
  if (el.dataset.listingId) return { listing: Number(el.dataset.listingId) };
  if (el.dataset.showcase) return { showcase: Number(el.dataset.showcase) };
  if (el.dataset.snap) return { snap: el.dataset.snap };
  const id = Number(el.dataset.creatureId ?? el.dataset.open);
  return Number.isFinite(id) ? { creature: id } : null;
}

function resolve(): { c: CreatureDTO; listing?: ListingDTO; shownBy?: string } | null {
  if (!showing) return null;
  if (showing.showcase !== undefined) {
    const b = S.broadcasts.find((x) => x.sid === showing!.showcase && x.creature);
    return b ? { c: b.creature!, shownBy: b.who ?? '' } : null;
  }
  if (showing.snap !== undefined) {
    const c = snapshots.get(showing.snap);
    return c ? { c, shownBy: '' } : null;
  }
  if (showing.listing !== undefined) {
    const l = S.market?.listings.find((x) => x.id === showing!.listing);
    return l && l.kind === 'creature' && l.snapshot ? { c: l.snapshot as CreatureDTO, listing: l } : null;
  }
  const c = S.creatures.get(showing.creature!);
  return c ? { c } : null;
}

function caughtWhen(ts: number) {
  const d = new Date(ts), today = new Date();
  const days = Math.round((new Date(today.toDateString()).getTime() - new Date(d.toDateString()).getTime()) / 86_400_000);
  return days === 0 ? 'today' : days === 1 ? 'yesterday' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
}

function caughtWhere(c: CreatureDTO) {
  if (c.ground.startsWith('boss')) return 'from a boss fight';
  if (c.ground === 'admin') return 'by an admin command';
  const g = GROUND[c.ground];
  return g ? `at ${esc(AREA[g.area].name)}` : '';
}

/** shownBy: the card is a chat showcase, a snapshot of someone's creature, so none of the viewer's own state applies. */
export function hoverCardHtml(c: CreatureDTO, listing?: ListingDTO, shownBy?: string) {
  const sp = SPECIES[c.species];
  const s = summary(c);
  const pos = linePos(c.species);
  const unused = sp.style === 'might' ? 3 : 1;
  const w = statWeights(c.species);
  const st = [s.stats.hp, s.stats.might, s.stats.guard, s.stats.focus, s.stats.will, s.stats.swift];
  const rf = refineOf(c), trn = trainOf(c);
  const t = S.trainer;
  const extra: string[] = [];
  if (!listing && shownBy === undefined && t) {
    const seat = (t.shrines ?? []).find((x) => x.active && x.posts.includes(c.id));
    if (seat) extra.push(`On ${esc(SHRINE_TIERS[seat.tier].name)} slot +${Math.round(SHRINE_TIERS[seat.tier].share * 100)}%`);
    if ((t.charmed ?? []).includes(c.id)) extra.push('Holds an XP Charm');
    if (c.listed) { const mine = S.market?.listings.find((l) => l.mine && (l.snapshot as CreatureDTO | null)?.id === c.id); extra.push(mine ? `For sale for ${full(mine.price)} coins` : 'For sale'); }
  }
  if (listing) extra.push(`For sale for ${full(listing.price)} ${listing.cur === 'gems' ? 'gems' : 'coins'} by ${esc(listing.seller)}`);
  if (shownBy) extra.push(`Shown in chat by ${esc(shownBy)}`);
  const pv = provenanceLine(c);
  return `<div class="top"><img src="${spriteUrl(c.species, c.rolls.aura)}" alt=""><div><b>${nameHtml(c)}</b> <span class="muted">Lv ${c.level}</span>
      <div class="sub">${sp.elements.map(elChip).join(' ')} Stage ${pos.at} of ${pos.of} · ${esc(sp.rarity)} species</div></div></div>
    ${rollPills(c)}
    <hr>
    <div class="sub" style="margin-bottom:4px">Talent</div>
    <div class="ivs">${c.rolls.ivs.map((v, i) => `<div${i === unused || !w[i] ? ' class="unused"' : ''}><small>${SHORT[i]}</small><b>${v}</b></div>`).join('')}</div>
    <hr>
    <div class="foot">${SHORT.map((n, i) => `${n} <b>${full(st[i])}</b>${rf[i] ? `<span class="rf">+${rf[i]}</span>` : ''}${trn[i] ? `<span class="tr">+${trn[i]}</span>` : ''}`).join(' · ')}</div>
    <div class="foot">Caught ${caughtWhen(c.caughtAt)} ${caughtWhere(c)}${boostMark(c)} · XP ${full(c.xp)} / ${full(creatureXpToNext(c.level))}</div>
    ${pv ? `<div class="foot">${pv}</div>` : ''}
    ${extra.length ? `<div class="foot">${extra.join(' · ')}</div>` : ''}`;
}

function keyOf(w: NonNullable<typeof showing>) {
  return w.listing !== undefined ? `l${w.listing}` : w.showcase !== undefined ? `s${w.showcase}` : w.snap !== undefined ? `p${w.snap}` : `c${w.creature}`;
}

function place() {
  if (!card || !anchor || !anchor.isConnected) return;
  placeBeside(card, anchor, { prefer: 'right', gap: 8 });
}

function open(el: HTMLElement) {
  const what = target(el);
  if (!what) return;
  anchor = el; showing = what;
  const hit = resolve();
  if (!hit) { close(); return; }
  const n = node();
  patch(n, `<div data-key="${keyOf(what)}">${hoverCardHtml(hit.c, hit.listing, hit.shownBy)}</div>`);
  n.hidden = false;
  place();
}

export function closeHoverCard() {
  clearTimeout(openTimer); clearTimeout(closeTimer);
  close();
}
function close() {
  anchor = null; showing = null;
  if (card) card.hidden = true;
}
function scheduleClose() {
  clearTimeout(closeTimer);
  closeTimer = window.setTimeout(close, CLOSE_MS);
}

export function hoverCardOpen() { return !!card && !card.hidden; }

/** Re-render in place after a game update; the card stays open. */
export function refreshHoverCard() {
  if (!card || card.hidden || !showing) return;
  const hit = resolve();
  if (!hit) return;
  patch(card, `<div data-key="${keyOf(showing)}">${hoverCardHtml(hit.c, hit.listing, hit.shownBy)}</div>`);
  place();
}

export function mountHoverCard() {
  document.addEventListener('pointerover', (e) => {
    if (e.pointerType === 'touch') return;
    const el = (e.target as HTMLElement).closest?.(ANCHOR) as HTMLElement | null;
    if (!el) {
      // A filter or an update can take the hovered creature off the page under a still mouse; no pointerout follows.
      if (hoverCardOpen() && anchor && !anchor.isConnected && !card!.contains(e.target as Node)) scheduleClose();
      return;
    }
    if (card && card.contains(el)) return;
    clearTimeout(closeTimer);
    if (el === anchor && hoverCardOpen()) return;
    clearTimeout(openTimer);
    openTimer = window.setTimeout(() => open(el), hoverCardOpen() ? 0 : OPEN_MS);
  });
  document.addEventListener('pointerout', (e) => {
    if (e.pointerType === 'touch') return;
    const el = (e.target as HTMLElement).closest?.(ANCHOR) as HTMLElement | null;
    if (!el) return;
    const to = e.relatedTarget as Node | null;
    if (to && (el.contains(to) || card?.contains(to))) return;
    clearTimeout(openTimer);
    if (hoverCardOpen()) scheduleClose();
  });
  // Touch: long-press opens the card as a popover; the tap that ends it is swallowed.
  document.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'touch') { return; }
    const el = (e.target as HTMLElement).closest?.(ANCHOR) as HTMLElement | null;
    swallowClick = false;
    if (card?.contains(e.target as Node)) return;
    if (hoverCardOpen()) close();
    if (!el) return;
    pressTimer = window.setTimeout(() => { swallowClick = true; open(el); }, PRESS_MS);
  });
  const cancelPress = () => clearTimeout(pressTimer);
  // A long-press may or may not be followed by a click; never let it eat a later tap.
  document.addEventListener('pointerup', (e) => { cancelPress(); if (swallowClick && e.pointerType === 'touch') setTimeout(() => { swallowClick = false; }, 350); });
  document.addEventListener('pointercancel', cancelPress);
  document.addEventListener('pointermove', (e) => { if (e.pointerType === 'touch') cancelPress(); });
  document.addEventListener('click', (e) => { if (swallowClick) { swallowClick = false; e.stopPropagation(); e.preventDefault(); } }, true);
  document.addEventListener('contextmenu', (e) => { if ((e.target as HTMLElement).closest?.(ANCHOR) && swallowClick) e.preventDefault(); });
  // Esc closes the card before anything else (a dialog, the creature page, a tab).
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && hoverCardOpen()) { e.preventDefault(); e.stopImmediatePropagation(); closeHoverCard(); }
  }, true);
  // Opening a creature (a click) or a dialog replaces the need for the card.
  document.addEventListener('click', () => { if (hoverCardOpen() && !swallowClick) close(); });
  window.addEventListener('scroll', () => { if (hoverCardOpen()) place(); }, true);
}
